import { SlashCommand } from '@@types/Api/Command';
import { CHANNEL_ID } from '@@types/common';
import Editor from '@@types/Editor';

import { EDITOR_ID, NEW_LINE_MANNER, PopperType } from '~/constants';
import { EDITOR_ORIGIN } from '~/constants/editor';
import { defaultEditorHints, editorHintsWithCommand, editorHintsWithCommandInPopup } from '~/hints/hints';
import GA from '~/services/GA/GA';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import DetailUiState from '~/stores/DetailUiState';
import { editorHintStore } from '~/stores/EditorHintStore';
import { emoticonStickerStore } from '~/stores/EmoticonStickerStore';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { slashCommandAppsStore } from '~/stores/SlashCommandAppsStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { extractCommandName } from '~/utils/channelLogsUtil';
import { clearValue, getBeforeChar, getValue, insertText, isEmptyEditor, setValue } from '~/utils/editorUtil';
import { convertNativeEmojiToColons } from '~/utils/emojiConvertor';
import { isNonNil } from '~/utils/fnUtil/fnUtil';
import { isEmpty, trimEnd } from 'lodash';
import { action, computed, runInAction } from 'mobx';
import { always, curry, ifElse, pipe, trim } from 'ramda';

import Actions from '../Actions';
import { replyLogStore } from '../ReplyLogStore';
import { stores } from '../Stores';

class MainEditorActions {
  private detailUiState: DetailUiState;

  constructor(
    private type: 'thread' | 'main',
    private actions: Actions,
    { detailUiState }: { detailUiState: DetailUiState }
  ) {
    this.detailUiState = detailUiState;
  }

  get contentsChannelId() {
    return this.detailUiState.contentsChannelId;
  }

  get logActions() {
    return this.type === 'main' ? this.actions.logActions : this.actions.threadLogActions;
  }

  @computed
  get hasReplyLog() {
    return isNonNil(replyLogStore.getByChannelId(this.contentsChannelId));
  }

  @computed
  get hints() {
    return !this.actions.permissionActions.canUseCommand || this.hasReplyLog
      ? defaultEditorHints
      : uiState.isMainView
      ? editorHintsWithCommand
      : editorHintsWithCommandInPopup;
  }

  get isNewLineEnter() {
    return globalSettingStore.newLineManner === NEW_LINE_MANNER.ENTER;
  }

  @action
  public handleChangeContentsChannelId = () => {
    this.detailUiState.maybeMainEditor().map((editor) => {
      this.actions.hintActions.clearEditorHint();
      if (this.detailUiState.prevChannelId) {
        this.storeCurrentValueDraftLog(this.detailUiState.prevChannelId, editor);
      }
      this.restoreDraftLog(this.contentsChannelId, this.type === 'thread' ? EDITOR_ID.THREAD_MAIN : EDITOR_ID.MAIN);
    });
  };

  @action
  public handleOnLoadEditor = (editor: Editor, editorId: EDITOR_ID) => {
    this.detailUiState.setMainEditor(editor);
    this.addEditorEventsHandlers(editor);
    this.restoreDraftLog(this.contentsChannelId, editorId);
    this.focus();
  };

  @action
  public handleBeforeDestroyEditor = async (channelId: CHANNEL_ID = this.detailUiState.contentsChannelId) => {
    const editor = this.detailUiState.maybeMainEditor().getOrElse(null);
    if (editor) {
      this.removeEditorEventHandlers(editor);
      this.detailUiState.setMainEditor(null);
      await this.storeCurrentValueDraftLog(channelId, editor);
    }
  };

  // 확인
  public focus = () => {
    this.detailUiState.maybeMainEditor().map((editor) => {
      this.actions.editorActions.focusEditor(editor);
    });
  };

  public storeCurrentValueDraftLog = async (channelId: string, editor: Editor) => {
    const text = getValue(editor);
    clearValue(editor);
    await this.actions.draftLogActions.set(channelId, trimEnd(text));
  };

  public restoreDraftLog = async (channelId: string, editorId: EDITOR_ID) => {
    const value = await this.actions.draftLogActions.get(channelId);
    const editor = this.detailUiState.maybeMainEditor().getOrElse(null);
    if (editor) {
      runInAction(() => {
        setValue(editor, value);
        this.detailUiState.setEmptyMainEditor(!value);
        if (uiState.currentContext === this.type) {
          this.actions.editorActions.focusAndGoToEndEditor(editor);
        }
        this.actions.editorActions.findAndSetEditorHint({ editor, hints: this.hints, editorId });
      });
    }
  };

  public handleOnEnter = (editor: Editor) => {
    if (!this.actions.editorActions.handleEnterKey(editor, NEW_LINE_MANNER.ENTER)) {
      this.submitText(this.getTextAndClear(editor));
    }
  };

  public handleOnShiftEnter = (editor: Editor) => {
    if (!this.actions.editorActions.handleEnterKey(editor, NEW_LINE_MANNER.SHIFT_ENTER)) {
      this.submitText(this.getTextAndClear(editor));
    }
  };

  public setMentionTrigger = () => {
    this.detailUiState.maybeMainEditor().map((editor) => {
      const mentionTrigger = this.getMentionTriggerText(editor);
      insertText(editor, mentionTrigger, EDITOR_ORIGIN.MENTION_TRIGGER_COMPLETE);
      this.actions.editorActions.focusEditor(editor);
    });
  };

  public setCommandTrigger = () => {
    this.detailUiState.maybeMainEditor().map((editor) => {
      insertText(editor, '/', EDITOR_ORIGIN.COMMAND_TRIGGER_COMPLETE);
      this.actions.editorActions.focusEditor(editor);
    });
  };

  private getMentionTriggerText = (editor: Editor) => {
    return pipe(getBeforeChar, trim, ifElse(isEmpty, always('@'), always(' @')))(editor);
  };

  private removeEditorEventHandlers = (editor: Editor): void => {
    this.actions.editorActions.resizeObserver.unobserve(editor.getWrapperElement().parentElement);
  };

  private addEditorEventsHandlers = (editor: Editor) => {
    this.actions.editorActions.resizeObserver.observe(editor.getWrapperElement().parentElement);
  };

  private closeActiveEmoticonDropdown = () => {
    emoticonStickerStore.activePopperState?.setOpen(false);
    uiState.closePopper(PopperType.StickerEmoticon);
  };

  public handleChange = (editor: Editor) => {
    if (coachMarkStore.isInCoachMark) return;

    this.detailUiState.setEmptyMainEditor(isEmptyEditor(editor));
  };

  private hasCommandByName = curry((channelId: string, commandName: string) => {
    if (this.type === 'thread') {
      const meetingAppIds = [userStore.meetingId, userStore.webexId];
      const appId = slashCommandAppsStore.getChannelCommandByName(commandName, channelId)?.integrationAppId;
      return (
        appId &&
        meetingAppIds.includes(slashCommandAppsStore.getChannelCommandByName(commandName, channelId)?.integrationAppId)
      );
    }
    return (
      stores.hasDoorayCommand(commandName) || slashCommandAppsStore.hasCommandByNameInChannel(commandName, channelId)
    );
  });

  private isCommandText = (channelId: string, text: string) => {
    const replyLog = replyLogStore.getByChannelId(channelId);
    if (isNonNil(replyLog)) return false;
    return pipe(extractCommandName, this.hasCommandByName(channelId))(text);
  };

  private hasIntegrationCommandId = (lastCommand: SlashCommand, name: string) => {
    return name === lastCommand?.name && !!lastCommand.integrationAppId;
  };

  private executeCommandName = (channelId: string, params) => {
    if (stores.hasDoorayCommand(params.name)) {
      this.actions.doorayCommandActions.executeCommand(channelId, params);
    } else {
      this.actions.slashCommandAppActions.executeCommand(channelId, params);
    }
  };

  private executeCommand = (channelId: string, text: string) => {
    const params = { name: extractCommandName(text), text };
    const lastCommand = editorHintStore.prevItem;

    if (this.hasIntegrationCommandId(lastCommand, params.name)) {
      this.actions.slashCommandAppActions.executeCommandById(channelId, { commandId: lastCommand.id, text });
    } else {
      this.executeCommandName(channelId, params);
    }

    editorHintStore.clearPrevItem();
    GA.writeExecuteCommand(params.name);
  };

  public getTextAndClear = (editor: Editor) => {
    const text = getValue(editor);
    text && clearValue(editor);
    return trimEnd(text);
  };

  @action
  public submitText = (text: string) => {
    this.handleSendText(this.contentsChannelId, convertNativeEmojiToColons(text));
    this.actions.draftLogActions.delete(this.contentsChannelId);
    this.closeActiveEmoticonDropdown();
  };

  private sendForwardLog = (channelId: string) => {
    const forwardLog = forwardLogStore.getByChannelId(channelId);
    return forwardLog ? this.logActions.sendForwardLog(channelId, forwardLog) : Promise.resolve();
  };

  private sendText = (channelId: string, text: string) => {
    const replyLog = replyLogStore.getByChannelId(channelId);
    if (isNonNil(replyLog)) {
      this.logActions.createAndReplyLog(channelId, replyLog, text);
      replyLogStore.removeReplyLog(channelId);
    } else {
      this.sendForwardLog(channelId).then(() => {
        this.logActions.createAndSendLog(channelId, text);
      });
    }
  };

  private handleSendText = async (channelId: string, text: string) => {
    await this.logActions.fetchLatestLogsIfNeeded(this.contentsChannelId);
    runInAction(() => {
      if (this.isCommandText(channelId, text)) {
        this.executeCommand(channelId, text);
      } else {
        this.sendText(channelId, text);
      }
    });
  };
}

export default MainEditorActions;
